import { createContext, FC, ReactNode, useContext, useEffect } from 'react'
import axios from 'axios'
import { useAuth } from '../core/Auth'
import { logoutUser } from '../core/_requests'
import { getStoredPushToken } from '../../apps/notificacoes/push/pushTokenStorage'
import { INACTIVITY_WARNING_WINDOW_MS } from './idleConfig'
import { markSessionLocked } from './sessionLockStorage'
import { InactivityStatus, useInactivityTimer } from './useInactivityTimer'

type InactivityContextValue = {
  status: InactivityStatus
  remainingSeconds: number
  unlock: () => void
  enabled: boolean
}

const InactivityContext = createContext<InactivityContextValue | undefined>(undefined)

/** Status que indicam que a sessão já não existe no backend — nada a revogar. */
const SESSAO_JA_INVALIDA = [401, 403]

export const InactivityProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { auth, currentUser, logout } = useAuth()

  const timeoutMs = (auth?.tempoInatividade ?? 0) * 60 * 1000
  const warningWindowMs = Math.min(INACTIVITY_WARNING_WINDOW_MS, timeoutMs / 2)
  const enabled = Boolean(auth?.accessToken && currentUser && timeoutMs > 0)

  const { status, remainingSeconds, unlock } = useInactivityTimer({
    timeoutMs,
    warningWindowMs,
    enabled,
  })

  useEffect(() => {
    if (status !== 'bloqueado') return

    markSessionLocked()
    logoutUser({ pushToken: getStoredPushToken() ?? undefined }).catch((error: unknown) => {
      const httpStatus = axios.isAxiosError(error) ? error.response?.status : undefined

      if (!httpStatus || !SESSAO_JA_INVALIDA.includes(httpStatus)) logout()
    })
  }, [status, logout])

  return (
    <InactivityContext.Provider value={{ status, remainingSeconds, unlock, enabled }}>
      {children}
    </InactivityContext.Provider>
  )
}

export const useInactivityContext = () => {
  const context = useContext(InactivityContext)
  if (!context) {
    throw new Error('useInactivityContext deve ser usado dentro de um InactivityProvider')
  }
  return context
}
